import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import useSeo from "@/lib/useSeo";
import RichText from "@/components/RichText";

const empty = { type: "appointment", first_name: "", last_name: "", email: "", phone: "", preferred_date: "", message: "" };

export default function Contact() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  useSeo({ title: "Contact Us | Healthy Roots Holistics", description: "Book an appointment or send an enquiry." });
  useEffect(() => { api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const c = settings?.contact || {};
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/public/enquiries", form);
      toast.success(c.success_message || data.message || "Enquiry sent!");
      setForm({ ...empty, type: form.type });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send. Please call us.");
    } finally { setSending(false); }
  };

  const phoneTel = settings?.phone_tel || "+12894421265";
  const phone = settings?.phone || "+1 (289) 442-1265";
  const email = settings?.email || "healthyroots4wellness@gmail.com";

  return (
    <div className="pt-28 pb-24" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-sm uppercase tracking-widest text-emerald-700 font-semibold">{c.eyebrow || "Get in Touch"}</span>
          <h1 className="text-4xl sm:text-5xl mt-2 text-emerald-950" data-testid="contact-heading">{c.heading || "Contact Us"}</h1>
          <div className="text-stone-600 mt-4">
            {c.intro ? <RichText html={c.intro} className="!text-stone-600" /> : <p>Thank you for your consideration. Please see our contact information below, or fill out the form.</p>}
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-4">
            {c.show_phone_card !== false && (
              <a href={`tel:${phoneTel}`} data-testid="contact-call-card" className="block bg-[#C85A32] text-white rounded-2xl p-6 hover:bg-[#b24d28] transition-colors">
                <div className="flex items-center gap-3 mb-1"><Phone size={20} /><span className="font-semibold" data-testid="contact-call-label">{c.phone_card_label || "Call Our Office"}</span></div>
                <p className="font-mono text-lg">{phone}</p>
              </a>
            )}
            {c.show_email_card !== false && (
              <a href={`mailto:${email}`} className="block bg-white rounded-2xl p-6 border border-stone-200/80 hover:border-emerald-300 transition-colors" data-testid="contact-email-card">
                <div className="flex items-center gap-3"><Mail className="text-emerald-700" /><div><p className="text-xs uppercase tracking-wide text-stone-500">{c.email_card_label || "Email Us"}</p><span className="font-mono text-sm break-all text-emerald-900">{email}</span></div></div>
              </a>
            )}
            {c.show_location_card !== false && (
              <div className="bg-white rounded-2xl p-6 border border-stone-200/80 flex items-center gap-3"><MapPin className="text-emerald-700" /><div><p className="text-xs uppercase tracking-wide text-stone-500">{c.location_card_label || "Service Area"}</p><span className="text-sm text-emerald-900">{settings?.address || "Hamilton, Ontario, Canada"}</span></div></div>
            )}
            {c.show_appointment_card !== false && (
              <div className="bg-white rounded-2xl p-6 border border-stone-200/80 flex items-center gap-3"><Clock className="text-emerald-700" /><div><p className="text-xs uppercase tracking-wide text-stone-500">{c.appointment_card_label || "By Appointment"}</p><span className="text-sm italic text-emerald-900">{settings?.appointment_note || "All visits and consultations by appointment only"}</span></div></div>
            )}
          </div>

          <form onSubmit={submit} className="lg:col-span-3 bg-white rounded-3xl p-8 border border-stone-200/80 shadow-sm" data-testid="enquiry-form">
            <h2 className="text-2xl text-emerald-900 mb-6">{c.form_heading || "Leave a message"}</h2>
            <div className="flex gap-2 mb-6">
              {[["appointment", c.appointment_tab_label || "Book an Appointment"], ["contact", c.contact_tab_label || "General Enquiry"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setForm({ ...form, type: v })} data-testid={`enquiry-type-${v}`}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${form.type === v ? "bg-emerald-800 text-white" : "bg-stone-100 text-emerald-900 hover:bg-stone-200"}`}>{l}</button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input label="First Name *" value={form.first_name} onChange={set("first_name")} required testid="input-first-name" />
              <Input label="Last Name *" value={form.last_name} onChange={set("last_name")} required testid="input-last-name" />
              <Input label="Email Address *" type="email" value={form.email} onChange={set("email")} required testid="input-email" />
              <Input label="Phone Number" value={form.phone} onChange={set("phone")} testid="input-phone" />
              {form.type === "appointment" && <Input label="Preferred Date / Time" value={form.preferred_date} onChange={set("preferred_date")} testid="input-preferred-date" />}
            </div>
            <label className="block mt-4">
              <span className="text-sm font-medium text-emerald-900">Message *</span>
              <textarea required rows={5} value={form.message} onChange={set("message")} data-testid="input-message"
                className="mt-1 w-full rounded-xl border border-stone-300 px-4 py-3 focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none" />
            </label>
            {(c.form_note || "This form sends an enquiry to our team — it does not complete a booking. We will contact you to confirm.") && (
              <p className="text-xs text-stone-500 mt-3">{c.form_note || "This form sends an enquiry to our team — it does not complete a booking. We will contact you to confirm."}</p>
            )}
            <button type="submit" disabled={sending} data-testid="enquiry-submit-btn"
              className="mt-5 w-full rounded-full bg-emerald-800 text-amber-50 py-3.5 font-semibold hover:bg-emerald-900 transition-colors disabled:opacity-60">
              {sending ? "Sending…" : form.type === "appointment" ? (c.submit_appointment_label || "Request Appointment") : (c.submit_contact_label || "Send Message")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Input({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-emerald-900">{label}</span>
      <input {...props} data-testid={testid}
        className="mt-1 w-full rounded-xl border border-stone-300 px-4 py-3 focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none" />
    </label>
  );
}
